"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useRouter } from "next/navigation";

interface User {
    id: string;
    email: string;
    role: "admin";
    name: string;
}

interface AuthContextType {
    user: User | null;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    token: string | null;
    isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const publicAuthState: AuthContextType = {
    user: null,
    login: async () => { throw new Error("Authentication is still loading."); },
    logout: async () => undefined,
    token: null,
    isLoading: true,
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [isHydrated, setIsHydrated] = useState(false);

    useEffect(() => setIsHydrated(true), []);

    if (!isHydrated) {
        return <AuthContext.Provider value={publicAuthState}>{children}</AuthContext.Provider>;
    }

    return <AuthClientProvider>{children}</AuthClientProvider>;
}

function AuthClientProvider({ children }: { children: React.ReactNode }) {
    const [token, setToken] = useState<string | null>(null);
    const router = useRouter();
    const demoHosts = (process.env.NEXT_PUBLIC_SHAIRA_DEMO_HOSTS || "localhost,127.0.0.1")
        .split(",")
        .map((host) => host.trim().toLowerCase())
        .filter(Boolean);
    const demoMode = process.env.NEXT_PUBLIC_SHAIRA_DEMO_MODE === "true"
        && demoHosts.includes(window.location.hostname.toLowerCase());
    const effectiveToken = demoMode ? "local-demo" : token;

    const currentUser = useQuery(
        api.auth.getCurrentUser,
        demoMode ? "skip" : effectiveToken ? { token: effectiveToken } : "skip"
    );

    const loginMutation = useMutation(api.auth.login);
    const logoutMutation = useMutation(api.auth.logout);

    // Load token from localStorage on mount
    useEffect(() => {
        const stored = localStorage.getItem("auth_token");
        if (stored) setToken(stored);
    }, []);

    const login = async (email: string, password: string) => {
        if (demoMode) {
            router.push("/admin");
            return;
        }
        const result = await loginMutation({ email, password });
        localStorage.setItem("auth_token", result.token);
        const secure = window.location.protocol === "https:" ? "; Secure" : "";
        // Keep the browser cookie aligned with the seven-day Convex session.
        document.cookie = `auth_token=${encodeURIComponent(result.token)}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax${secure}`;
        setToken(result.token);
        router.push("/admin");
    };

    const logout = async () => {
        if (demoMode) {
            router.push("/");
            return;
        }
        if (token) {
            await logoutMutation({ token });
        }
        localStorage.removeItem("auth_token");
        // Clear cookie
        document.cookie = "auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
        setToken(null);
        router.push("/login");
    };

    return (
        <AuthContext.Provider
            value={{
                user: demoMode
                    ? { id: "local-demo", email: "demo@localhost", role: "admin" as const, name: "Local Demo" }
                    : currentUser?.role === "admin" ? { ...currentUser, role: "admin" as const } : null,
                login,
                logout,
                token: effectiveToken,
                isLoading: demoMode ? false : currentUser === undefined && !!effectiveToken,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within AuthProvider");
    return context;
};
